import { useState, useRef } from 'react'
import { X, Upload, Download, AlertTriangle, CheckCircle, Loader, Plus, RefreshCw } from 'lucide-react'
import { addProduct, updateProduct, getProducts } from '../../data/products'
import { useToast } from '../../context/ToastContext'
import { extractPdfText, parsePosLine } from '../../lib/pdfPosExtract'

// Bulk product upload modal — accepts .xlsx, .xls, .csv, or .pdf files.
//
// Two source formats are supported and auto-detected:
//
//  1. TEMPLATE format (Excel/CSV): the spreadsheet shipped via Download
//     Template. Columns: Code, Name, Category, Price (R), Active,
//     and optional Sale Type / Unit / QPS / Pap Cost.
//
//  2. POS format (Excel/CSV/PDF): a Staff POS Sales report exported
//     from the POS system. Columns: Group / Qty / Sales R / %Sales /
//     Cost R / Profit R. Section headers (Bakery, Kitchen, Sandwich,
//     etc.) appear as standalone rows above their products and become
//     the imported product's category. Some rows have a numeric SKU
//     prefix on the name which is stripped. unitPrice is computed as
//     sales / qty (the average selling price across the report).
//
// Existing products with matching codes are UPDATED (preserving their
// createdAt and any fields not present in the upload). New codes are
// CREATED. Invalid rows are skipped — they do not block the import.

const VALID_SALE_TYPES = ['discrete', 'continuous_mirror', 'continuous_fixed', 'fixed_unit']
const VALID_UNITS = ['units', 'rands', 'kg']

// Section labels recognised in POS reports. Anything else on its own
// line is ignored as decorative text. The first match becomes the
// current category for all subsequent rows until the next match.
const POS_SECTION_LABELS = [
  'kitchen', 'bakery', 'sandwich', 'sandwiches',
  'soft drink', 'soft drinks', 'tea', 'sweets',
  'miscellaneous', 'misc', 'pin', 'cff',
  'tuckshop', 'tuck shop', 'yawehs', "yaweh's delight",
]

// ─── Header detection ──────────────────────────────────────────────

function mapHeaderToField(header) {
  const h = String(header || '').toLowerCase().trim()
  if (h === 'code') return 'code'
  if (h === 'name') return 'name'
  if (h === 'category') return 'category'
  if (h === 'price' || h === 'price (r)' || h === 'unit price' || h === 'unitprice') return 'unitPrice'
  if (h === 'limpopo' || h === 'limpopo price' || h === 'limpopoprice') return 'limpopoPrice'
  if (h === 'pretoria' || h === 'pretoria price' || h === 'pretoriaprice') return 'pretoriaPrice'
  if (h === 'active') return 'active'
  if (h === 'sale type' || h === 'saletype') return 'saleType'
  if (h === 'unit') return 'unit'
  if (h === 'qps') return 'qps'
  if (h === 'pap cost' || h === 'papcost') return 'papCost'
  return null
}

// Look at a header row and decide whether it's a POS export. POS
// reports always have Qty + Sales R columns (with or without Group/%).
function isPosHeaderRow(headers) {
  const lower = headers.map(h => String(h || '').toLowerCase().trim())
  const hasQty = lower.some(h => h === 'qty' || h === 'quantity')
  const hasSales = lower.some(h => h === 'sales r' || h === 'sales' || h === 'sales (r)')
  return hasQty && hasSales
}

// Strip a leading numeric SKU like "60069795 MATCHES" → "MATCHES".
function stripSkuPrefix(name) {
  return String(name || '').replace(/^\d+\s+/, '').trim()
}

// Generate a deterministic short product code from a display name.
function generateCodeFromName(name) {
  return String(name || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 12) || 'IMPORTED'
}

// ─── Row validation ────────────────────────────────────────────────

// Parse one normalised row object into a product record + validation
// result. The validator does NOT treat duplicate codes as errors any
// more — that classification is done at the action stage so existing
// products can be updated.
function validateRow(rawRow, idx, allCodesSoFar) {
  const issues = []
  const codeRaw = String(rawRow.code ?? '').trim()
  if (!codeRaw) issues.push('Code is required')
  const code = codeRaw.toUpperCase().replace(/\s+/g, '_')

  const name = String(rawRow.name ?? '').trim()
  if (!name) issues.push('Name is required')

  const category = String(rawRow.category ?? '').toLowerCase().trim()
  if (!category) issues.push('Category is required')

  const parsePriceCell = (raw) => {
    if (raw === undefined || raw === '' || raw === null) return undefined
    const cleaned = String(raw).replace(/[R,\s]/gi, '')
    if (/^none|not\s*sold|n\/a|-$/i.test(String(raw).trim())) return null
    const n = parseFloat(cleaned)
    return Number.isFinite(n) && n >= 0 ? n : NaN
  }

  const limpopoParsed = parsePriceCell(rawRow.limpopoPrice)
  const pretoriaParsed = parsePriceCell(rawRow.pretoriaPrice)
  const hasDualColumns = rawRow.limpopoPrice !== undefined || rawRow.pretoriaPrice !== undefined
  if (hasDualColumns) {
    if (Number.isNaN(limpopoParsed)) issues.push('Limpopo price must be a number, blank, or "none"')
    if (Number.isNaN(pretoriaParsed)) issues.push('Pretoria price must be a number, blank, or "none"')
  }

  const priceRaw = rawRow.unitPrice
  let unitPrice = null
  if (priceRaw === undefined || priceRaw === '' || priceRaw === null) {
    // Fall back to whichever branch price is populated.
    const fallback = [limpopoParsed, pretoriaParsed].find(v => typeof v === 'number')
    if (typeof fallback === 'number') unitPrice = fallback
    else if (!hasDualColumns) issues.push('Price is required')
  } else {
    const cleaned = String(priceRaw).replace(/[R,\s]/g, '')
    const n = parseFloat(cleaned)
    if (!Number.isFinite(n) || n < 0) {
      issues.push('Price must be a positive number')
    } else {
      unitPrice = n
    }
  }

  // Build prices map when either dual column is present. Undefined
  // values (blank cell, no column) default to null = not sold at that
  // branch. Numeric values pass through.
  let prices = null
  if (hasDualColumns) {
    prices = {
      limpopo: typeof limpopoParsed === 'number' ? limpopoParsed : null,
      pretoria: typeof pretoriaParsed === 'number' ? pretoriaParsed : null,
    }
  }

  const activeRaw = String(rawRow.active ?? '').toLowerCase().trim()
  const isActive = activeRaw === '' || activeRaw === 'yes' || activeRaw === 'y'
    || activeRaw === 'true' || activeRaw === '1'

  let saleType = 'discrete'
  if (rawRow.saleType !== undefined && String(rawRow.saleType).trim() !== '') {
    const stRaw = String(rawRow.saleType).toLowerCase().trim().replace(/\s+/g, '_')
    if (VALID_SALE_TYPES.includes(stRaw)) saleType = stRaw
    else issues.push(`Sale type "${rawRow.saleType}" not recognised — use one of: ${VALID_SALE_TYPES.join(', ')}`)
  }

  let unit = 'units'
  if (rawRow.unit !== undefined && String(rawRow.unit).trim() !== '') {
    const uRaw = String(rawRow.unit).toLowerCase().trim()
    if (VALID_UNITS.includes(uRaw)) unit = uRaw
    else issues.push(`Unit "${rawRow.unit}" not recognised — use one of: ${VALID_UNITS.join(', ')}`)
  }

  let qps = null
  if (rawRow.qps !== undefined && String(rawRow.qps).trim() !== '') {
    const n = parseFloat(String(rawRow.qps).replace(/\s/g, ''))
    if (!Number.isFinite(n) || n < 0) issues.push('QPS must be a positive number')
    else qps = n
  }

  let papCost = null
  if (rawRow.papCost !== undefined && String(rawRow.papCost).trim() !== '') {
    const n = parseFloat(String(rawRow.papCost).replace(/[R,\s]/g, ''))
    if (!Number.isFinite(n) || n < 0) issues.push('Pap cost must be a positive number')
    else papCost = n
  }

  // Duplicate within this upload — that IS still an error (can't import
  // the same code twice in one batch).
  if (code) {
    const codeLower = code.toLowerCase()
    if (allCodesSoFar.has(codeLower)) {
      issues.push(`Duplicate code "${code}" appears earlier in this upload`)
    }
  }

  return {
    rowNumber: idx + 1,
    code,
    name,
    category,
    unitPrice,
    prices,
    isActive,
    saleType,
    unit,
    qps,
    papCost,
    issues,
    valid: issues.length === 0,
  }
}

// ─── Template parsers (CSV / Excel) ────────────────────────────────

function parseTemplateCsv(text) {
  const allLines = text.split(/\r?\n/)
  const lines = allLines.map(l => l.trim()).filter(l => l && !l.startsWith('#'))
  if (lines.length < 2) return []
  const headers = lines[0].split(',').map(h => h.trim())
  const fieldKeys = headers.map(mapHeaderToField)
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim())
    const obj = {}
    fieldKeys.forEach((field, i) => { if (field) obj[field] = values[i] ?? '' })
    return obj
  })
}

async function parseTemplateExcel(arrayBuffer) {
  const XLSX = await import('xlsx')
  const workbook = XLSX.read(arrayBuffer, { type: 'array' })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) return []
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  if (rawRows.length < 2) return []
  const dataRows = rawRows.filter(r => {
    const first = String(r[0] || '').trim()
    return first && !first.startsWith('#')
  })
  if (dataRows.length < 2) return []
  const headers = dataRows[0].map(h => String(h || '').trim())
  const fieldKeys = headers.map(mapHeaderToField)
  return dataRows.slice(1).map(row => {
    const obj = {}
    fieldKeys.forEach((field, i) => { if (field) obj[field] = String(row[i] ?? '').trim() })
    return obj
  })
}

// ─── POS parsers (PDF / Excel / CSV) ───────────────────────────────

// Convert a sequence of plain text lines into normalised raw row
// objects, tracking section headers as the current category.
function parsePosLines(lines) {
  const rows = []
  let currentSection = 'imported'
  for (const rawLine of lines) {
    const line = String(rawLine || '').trim()
    if (!line) continue

    // Section header? Standalone line whose lowercase text matches
    // a known POS section label.
    const lower = line.toLowerCase()
    if (POS_SECTION_LABELS.some(s => lower === s)) {
      currentSection = lower.replace(/\s+/g, '_')
      continue
    }

    const parsed = parsePosLine(line)
    if (!parsed) continue
    if (!parsed.qty || parsed.qty <= 0) continue
    if (!Number.isFinite(parsed.sales) || parsed.sales <= 0) continue

    const cleanName = stripSkuPrefix(parsed.name)
    if (!cleanName) continue

    const code = generateCodeFromName(cleanName)
    const unitPrice = Math.round((parsed.sales / parsed.qty) * 100) / 100

    rows.push({
      code,
      name: cleanName,
      category: currentSection,
      unitPrice,
      active: 'YES',
    })
  }
  return rows
}

// POS Excel: read the first sheet as a 2-D array, then flatten each
// row into a single text line (joined with multi-space separators)
// and run it through the same line parser as the PDF path.
async function parsePosExcel(arrayBuffer) {
  const XLSX = await import('xlsx')
  const workbook = XLSX.read(arrayBuffer, { type: 'array' })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) return []
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  const lines = rawRows.map(r =>
    r.map(c => String(c ?? '').trim()).filter(Boolean).join('   ')
  )
  return parsePosLines(lines)
}

function parsePosCsv(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l =>
    l.split(',').map(c => c.trim()).filter(Boolean).join('   ')
  )
  return parsePosLines(lines)
}

// ─── Format detection ──────────────────────────────────────────────

// Peek at an Excel/CSV header row to decide template vs POS. Returns
// 'pos' or 'template'.
async function detectExcelFormat(arrayBuffer) {
  const XLSX = await import('xlsx')
  const workbook = XLSX.read(arrayBuffer, { type: 'array' })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) return 'template'
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  for (const r of rawRows) {
    const first = String(r[0] || '').trim()
    if (!first || first.startsWith('#')) continue
    const headers = r.map(c => String(c || '').trim())
    if (isPosHeaderRow(headers)) return 'pos'
    if (headers.some(h => mapHeaderToField(h) === 'code')) return 'template'
    // First real row is neither — treat as POS (numeric data, no headers)
    return 'pos'
  }
  return 'template'
}

function detectCsvFormat(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l && !l.startsWith('#'))
  if (lines.length === 0) return 'template'
  const headers = lines[0].split(',').map(h => h.trim())
  if (isPosHeaderRow(headers)) return 'pos'
  if (headers.some(h => mapHeaderToField(h) === 'code')) return 'template'
  return 'pos'
}

// ─── Template download ────────────────────────────────────────────

function downloadTemplate() {
  const template = [
    '# Desres Kitchen — Product Upload Template',
    '# Code: unique code (no spaces, will be uppercased)',
    '# Name: display name',
    '# Category: existing category name (lowercase, e.g. mukokoroshi, pastry)',
    '# Price (R): number only, no R symbol — used as the legacy fallback',
    '# Limpopo / Pretoria: branch-specific price. Blank or "none" = not sold at branch',
    '# Active: YES or NO',
    '# Sale Type (optional): discrete | continuous_mirror | continuous_fixed | fixed_unit',
    '# Unit (optional): units | rands | kg',
    '# QPS (optional): quantity per serving (number)',
    '# Pap Cost (optional, kitchen only): pap cost in rands',
    'Code,Name,Category,Price (R),Limpopo,Pretoria,Active,Sale Type,Unit,QPS,Pap Cost',
    'EXAMPLE,Example Product,mukokoroshi,25,25,none,YES,discrete,units,,',
  ].join('\n')

  const blob = new Blob([template], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'desres_product_template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── Component ────────────────────────────────────────────────────

export default function BulkProductUpload({ department, onClose, onProductsAdded }) {
  const { addToast } = useToast()
  const [parsedRows, setParsedRows] = useState([])
  const [parseError, setParseError] = useState('')
  const [parseProgress, setParseProgress] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedCount, setSavedCount] = useState(0)
  const [detectedFormat, setDetectedFormat] = useState(null) // 'template' | 'pos' | null
  const fileInputRef = useRef(null)

  const [selectedDept, setSelectedDept] = useState(department || 'kitchen')

  const handleFile = async (file) => {
    setParseError('')
    setParsedRows([])
    setSavedCount(0)
    setDetectedFormat(null)
    setParseProgress('')
    if (!file) return

    const name = file.name.toLowerCase()
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls')
    const isCsv = name.endsWith('.csv')
    const isPdf = name.endsWith('.pdf')
    if (!isExcel && !isCsv && !isPdf) {
      setParseError('Please upload a .xlsx, .xls, .csv or .pdf file')
      return
    }

    try {
      let rawObjects = []
      let format = 'template'

      if (isPdf) {
        format = 'pos'
        const text = await extractPdfText(file, msg => setParseProgress(msg))
        const lines = text.split(/\r?\n/)
        rawObjects = parsePosLines(lines)
      } else if (isExcel) {
        const buffer = await file.arrayBuffer()
        format = await detectExcelFormat(buffer)
        rawObjects = format === 'pos'
          ? await parsePosExcel(buffer)
          : await parseTemplateExcel(buffer)
      } else {
        const text = await file.text()
        format = detectCsvFormat(text)
        rawObjects = format === 'pos'
          ? parsePosCsv(text)
          : parseTemplateCsv(text)
      }

      setDetectedFormat(format)
      setParseProgress('')

      if (rawObjects.length === 0) {
        setParseError('The file appears to be empty or has no data rows')
        return
      }

      // Validate each row, then classify as create / update / skip
      // by comparing against existing products in the selected dept.
      const existingByCode = new Map()
      for (const p of getProducts(selectedDept)) {
        existingByCode.set(p.code.toLowerCase(), p)
      }

      const seenCodesLower = new Set()
      const validated = rawObjects.map((row, i) => {
        const result = validateRow(row, i, seenCodesLower)
        if (result.code) seenCodesLower.add(result.code.toLowerCase())
        let action = 'skip'
        if (result.valid) {
          action = existingByCode.has(result.code.toLowerCase()) ? 'update' : 'create'
        }
        return { ...result, action }
      })

      const usable = validated.filter(r => r.action !== 'skip').length
      if (usable === 0) {
        setParseError('No valid product rows found. Check the file format matches the template or POS export.')
      }
      setParsedRows(validated)
    } catch (err) {
      console.error('[BulkProductUpload] parse error:', err)
      setParseError(`Failed to parse file: ${err?.message || 'unknown error'}`)
      setParseProgress('')
    }
  }

  const handleSave = async () => {
    const importable = parsedRows.filter(r => r.action === 'create' || r.action === 'update')
    if (importable.length === 0) return
    setSaving(true)
    setSavedCount(0)
    let created = 0
    let updated = 0
    let failed = 0
    let done = 0

    for (const row of importable) {
      try {
        const payload = {
          code: row.code,
          name: row.name,
          category: row.category,
          unitPrice: row.unitPrice,
          qps: row.qps,
          saleType: row.saleType,
          unit: row.unit,
          ...(row.prices ? { prices: row.prices } : {}),
          ...(selectedDept === 'kitchen' && row.papCost != null ? { papCost: row.papCost } : {}),
          deleted: !row.isActive,
        }

        if (row.action === 'update') {
          const result = await updateProduct(row.code, payload, selectedDept)
          if (result) updated++
          else {
            // Existing product disappeared between parse and save —
            // fall back to create so the row is not lost.
            await addProduct(payload, selectedDept)
            created++
          }
        } else {
          await addProduct(payload, selectedDept)
          created++
        }
      } catch (err) {
        console.warn('[BulkProductUpload] save failed for', row.code, err?.message)
        failed++
      }
      done++
      setSavedCount(done)
    }

    setSaving(false)
    const skipped = parsedRows.length - importable.length
    const parts = []
    if (created > 0) parts.push(`${created} added`)
    if (updated > 0) parts.push(`${updated} updated`)
    if (skipped > 0) parts.push(`${skipped} skipped`)
    if (failed > 0) parts.push(`${failed} failed`)
    const summary = parts.length ? parts.join(', ') : 'no changes'

    addToast(
      failed === 0 ? `Import complete — ${summary}` : `Import finished with errors — ${summary}`,
      failed === 0 ? 'success' : 'warning'
    )
    onProductsAdded?.()
    if (failed === 0) onClose()
  }

  const createCount = parsedRows.filter(r => r.action === 'create').length
  const updateCount = parsedRows.filter(r => r.action === 'update').length
  const skipCount = parsedRows.filter(r => r.action === 'skip').length
  const importableCount = createCount + updateCount

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-white">Bulk Upload Products</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload an Excel, CSV or PDF file to add or update multiple products
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Department selector + template download */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-gray-300">
              Department:
              <select
                value={selectedDept}
                onChange={e => setSelectedDept(e.target.value)}
                disabled={saving}
                className="ml-2 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
              >
                <option value="kitchen">Kitchen</option>
                <option value="bakery">Bakery</option>
                <option value="yawehs">Yaweh's Delight</option>
                <option value="tuckshop">Tuck Shop</option>
              </select>
            </label>
            <button
              type="button"
              onClick={downloadTemplate}
              className="flex items-center gap-1.5 ml-auto bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded text-xs"
            >
              <Download size={13} /> Download Template
            </button>
          </div>

          {/* Instructions */}
          <div className="bg-gray-800/50 border border-gray-700 rounded p-3 text-xs text-gray-400 space-y-1">
            <p className="font-medium text-gray-300">Instructions</p>
            <p>
              Upload either the <span className="text-gray-300">template format</span> (Code, Name, Category, Price, Active)
              or a <span className="text-gray-300">POS export</span> (Excel/CSV/PDF) — the format is detected automatically.
            </p>
            <p>
              Existing products with matching codes are <span className="text-blue-300">updated</span>.
              New codes are <span className="text-green-300">added</span>. Lines starting with{' '}
              <code className="text-amber-400">#</code> are treated as comments.
            </p>
          </div>

          {/* File input */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.pdf,application/pdf,application/x-pdf"
              disabled={saving}
              onChange={e => handleFile(e.target.files?.[0])}
              className="block w-full text-sm text-gray-400
                file:mr-4 file:py-2 file:px-4 file:rounded file:border-0
                file:bg-brand-600 file:text-white file:cursor-pointer
                hover:file:bg-brand-500"
            />
          </div>

          {/* Parse progress (PDF only) */}
          {parseProgress && !parsedRows.length && !parseError && (
            <div className="bg-gray-800/50 border border-gray-700 rounded px-4 py-3 flex items-center gap-2">
              <Loader size={14} className="animate-spin text-brand-400" />
              <p className="text-sm text-gray-300">{parseProgress}</p>
            </div>
          )}

          {/* Parse error */}
          {parseError && (
            <div className="bg-red-950/40 border border-red-800/50 rounded px-4 py-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{parseError}</p>
            </div>
          )}

          {/* Format banner */}
          {detectedFormat && parsedRows.length > 0 && (
            <div className="bg-gray-800/50 border border-gray-700 rounded px-4 py-2 text-xs text-gray-400">
              {detectedFormat === 'pos'
                ? 'Detected POS export — codes auto-generated from product names, prices computed as Sales R ÷ Qty.'
                : 'Detected template format.'}
            </div>
          )}

          {/* Preview table */}
          {parsedRows.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-white">Preview</h3>
                <p className="text-xs text-gray-400">
                  <span className="text-green-400">{createCount}</span> new,{' '}
                  <span className="text-blue-400">{updateCount}</span> updates,{' '}
                  <span className="text-red-400">{skipCount}</span> skipped
                </p>
              </div>
              <div className="overflow-x-auto border border-gray-800 rounded max-h-96">
                <table className="w-full text-xs">
                  <thead className="bg-gray-800/50 text-gray-400 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-2 w-20">Action</th>
                      <th className="text-left py-2 px-2">Code</th>
                      <th className="text-left py-2 px-2">Name</th>
                      <th className="text-left py-2 px-2">Category</th>
                      <th className="text-right py-2 px-2">Price</th>
                      <th className="text-center py-2 px-2">Active</th>
                      <th className="text-left py-2 px-2">Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((row) => (
                      <tr
                        key={row.rowNumber}
                        className={`border-t border-gray-800/60 ${
                          row.action === 'skip' ? 'bg-red-950/20'
                          : row.action === 'update' ? 'bg-blue-950/10'
                          : ''
                        }`}
                      >
                        <td className="py-1.5 px-2">
                          {row.action === 'create' && (
                            <span className="inline-flex items-center gap-1 text-green-400">
                              <Plus size={11} /> New
                            </span>
                          )}
                          {row.action === 'update' && (
                            <span className="inline-flex items-center gap-1 text-blue-400">
                              <RefreshCw size={11} /> Update
                            </span>
                          )}
                          {row.action === 'skip' && (
                            <span className="inline-flex items-center gap-1 text-red-400">
                              <AlertTriangle size={11} /> Skip
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 px-2 font-mono text-gray-300">{row.code || '—'}</td>
                        <td className="py-1.5 px-2 text-gray-300">{row.name || '—'}</td>
                        <td className="py-1.5 px-2 text-gray-400 capitalize">{row.category || '—'}</td>
                        <td className="py-1.5 px-2 text-right text-gray-300">
                          {row.unitPrice != null ? `R${row.unitPrice}` : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-center text-gray-400">
                          {row.isActive ? 'Yes' : 'No'}
                        </td>
                        <td className="py-1.5 px-2 text-red-400">
                          {row.issues.length > 0 ? row.issues.join('; ') : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 gap-3">
          <p className="text-xs text-gray-500">
            {saving && (
              <span className="flex items-center gap-2">
                <Loader size={12} className="animate-spin" />
                Saving {savedCount} of {importableCount}...
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || importableCount === 0}
              className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2 rounded text-sm font-medium"
            >
              <Upload size={14} />
              {saving ? `Importing... (${savedCount}/${importableCount})` : `Import ${importableCount} Products`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
